import { useEffect, useRef, useState } from 'react'
import Router, { useRouter } from 'next/router'

/**
 * Restauración de posición en listados (PLP / SRP) al volver atrás desde la PDP.
 *
 * Next.js (Pages Router, sin `experimental.scrollRestoration`) hace scroll a
 * (0,0) en cada navegación, incluida la de "atrás". Antes la PLP lo compensaba
 * con `localStorage` + `MutationObserver`, pero eso dependía de que el tile
 * siguiera en memoria: tras una recarga (Android descartando la pestaña, el
 * reload por ChunkLoadError después de un deploy, etc.) solo existen las dos
 * primeras páginas y la posición se perdía.
 *
 * Aquí se guarda en `sessionStorage`, por URL y por entrada de historial, el
 * SKU clicado, el `scrollY` y la cantidad de páginas cargadas. Al volver a la
 * misma entrada de historial se rehidratan esas páginas (`restoreSize`) y se
 * vuelve al SKU (o al `scrollY` como respaldo).
 */

export type PLPScrollEntry = {
    /** SKU del último producto clicado en el listado (id del tile). */
    sku: string | null
    scrollY: number
    /** `size` de `useSWRInfinite` al salir (páginas cargadas, prefetch incluido). */
    size: number
    /** `history.state.key` de Next para la entrada del listado. */
    key: string | null
    ts: number
}

export const PLP_SCROLL_STORAGE_PREFIX = 'plp-scroll:'
export const PLP_SCROLL_MAX_AGE_MS = 30 * 60 * 1000
export const PLP_SCROLL_MAX_RESTORE_PAGES = 12
export const PLP_SCROLL_RESTORE_TIMEOUT_MS = 12000
export const PLP_SCROLL_READY_POLL_MS = 250

let lastClickedSku: string | null = null

/** Registra el SKU del tile clicado para que la restauración apunte a él. */
export const rememberPlpProductClick = (sku?: string | number | null): void => {
    lastClickedSku = sku !== undefined && sku !== null ? String(sku) : null
}

/** Solo para tests. */
export const resetPlpScrollRestoreState = (): void => {
    lastClickedSku = null
}

const storageKey = (asPath: string): string =>
    `${PLP_SCROLL_STORAGE_PREFIX}${asPath}`

const setScrollRestoration = (mode: ScrollRestoration): void => {
    try {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = mode
        }
    } catch {
        // noop
    }
}

const getHistoryKey = (): string | null => {
    if (typeof window === 'undefined') return null
    const state = window.history.state as { key?: string } | null
    return state?.key ?? null
}

export const readPlpScrollEntry = (asPath: string): PLPScrollEntry | null => {
    if (typeof window === 'undefined') return null
    try {
        const raw = window.sessionStorage.getItem(storageKey(asPath))
        if (!raw) return null
        const entry = JSON.parse(raw) as PLPScrollEntry
        if (!entry || typeof entry.scrollY !== 'number') return null
        if (Date.now() - (entry.ts ?? 0) > PLP_SCROLL_MAX_AGE_MS) {
            window.sessionStorage.removeItem(storageKey(asPath))
            return null
        }
        return entry
    } catch {
        return null
    }
}

export const savePlpScrollEntry = (
    asPath: string,
    entry: Omit<PLPScrollEntry, 'ts'>
): void => {
    if (typeof window === 'undefined') return
    try {
        window.sessionStorage.setItem(
            storageKey(asPath),
            JSON.stringify({ ...entry, ts: Date.now() })
        )
    } catch {
        // sessionStorage lleno o deshabilitado: la restauración es best-effort
    }
}

export const clearPlpScrollEntry = (asPath: string): void => {
    if (typeof window === 'undefined') return
    try {
        window.sessionStorage.removeItem(storageKey(asPath))
    } catch {
        // noop
    }
}

type UsePLPScrollRestoreEntryResult = {
    /** Entrada pendiente de restaurar para la URL/entrada de historial actual. */
    entry: PLPScrollEntry | null
    /** Páginas a rehidratar (`initialSize` de SWR); `undefined` si no aplica. */
    restoreSize: number | undefined
}

/**
 * Lee (una sola vez por montaje) la entrada guardada para la URL actual. Solo
 * aplica si la entrada de historial coincide: así "atrás" y la recarga de esa
 * misma entrada restauran, pero llegar al listado por menú o breadcrumb no.
 */
export const usePLPScrollRestoreEntry = (
    initialSize = 1
): UsePLPScrollRestoreEntryResult => {
    const { asPath } = useRouter()
    const [entry] = useState<PLPScrollEntry | null>(() => {
        if (typeof window === 'undefined') return null
        const stored = readPlpScrollEntry(asPath)
        if (!stored) return null
        if (stored.key && stored.key !== getHistoryKey()) return null
        // Mientras haya una restauración pendiente el navegador no debe aplicar
        // la suya: Chrome la difiere y, con pocas páginas cargadas, aterriza en
        // el fondo del listado y encadena cargas del scroll infinito. Vuelve a
        // 'auto' en la siguiente navegación (usePLPScrollRestore).
        setScrollRestoration('manual')
        return stored
    })

    const restoreSize =
        entry && entry.size > initialSize
            ? Math.min(entry.size, PLP_SCROLL_MAX_RESTORE_PAGES)
            : undefined

    return { entry, restoreSize }
}

type UsePLPScrollRestoreOptions = {
    entry: PLPScrollEntry | null
    /** `size` actual de `useSWRInfinite` (se guarda al salir del listado). */
    size: number
    /** Los productos esperados ya están renderizados y no se está cargando. */
    ready: boolean
    /** Compensación por cabecera/filtros fijos al hacer scroll al tile. */
    scrollOffset?: number
    enabled?: boolean
}

/**
 * Guarda la posición al salir del listado (navegación no shallow o descarga de
 * la página) y aplica la restauración pendiente cuando `ready` es true, con un
 * timeout de respaldo que usa `scrollY` si el tile nunca aparece.
 */
export const usePLPScrollRestore = ({
    entry,
    size,
    ready,
    scrollOffset = 0,
    enabled = true
}: UsePLPScrollRestoreOptions): { isRestoring: boolean } => {
    const { asPath } = useRouter()
    const sizeRef = useRef(size)
    sizeRef.current = size
    const asPathRef = useRef(asPath)
    asPathRef.current = asPath
    const [isRestoring, setIsRestoring] = useState(Boolean(entry))
    const doneRef = useRef(false)

    useEffect(() => {
        if (!enabled || typeof window === 'undefined') return undefined

        const save = () => {
            savePlpScrollEntry(asPathRef.current, {
                sku: lastClickedSku,
                scrollY: window.scrollY,
                size: sizeRef.current,
                key: getHistoryKey()
            })
        }
        const onRouteChangeStart = (
            _url: string,
            options?: { shallow?: boolean }
        ) => {
            if (options?.shallow) return
            save()
            lastClickedSku = null
            // La restauración nativa se desactivó mientras había una entrada
            // pendiente (ver usePLPScrollRestoreEntry); el resto del sitio la
            // recupera al salir del listado.
            setScrollRestoration('auto')
        }

        Router.events?.on?.('routeChangeStart', onRouteChangeStart)
        window.addEventListener('pagehide', save)
        return () => {
            Router.events?.off?.('routeChangeStart', onRouteChangeStart)
            window.removeEventListener('pagehide', save)
        }
    }, [enabled])

    const readyRef = useRef(ready)
    readyRef.current = ready
    const applyRef = useRef<(() => void) | null>(null)

    useEffect(() => {
        if (!entry || doneRef.current || typeof window === 'undefined') {
            return undefined
        }

        let cancelled = false
        /**
         * `force` solo al vencer el timeout: mientras tanto, si el tile del SKU
         * aún no está en el DOM (la grilla pinta las páginas rehidratadas un
         * ciclo después de que termina la carga) se sigue esperando.
         */
        const apply = (force = false) => {
            if (cancelled || doneRef.current) return
            const element = entry.sku
                ? document.getElementById(entry.sku)
                : null
            if (!force && entry.sku && !element) return
            doneRef.current = true
            window.scrollTo({
                top: element
                    ? Math.max(element.offsetTop - scrollOffset, 0)
                    : entry.scrollY,
                left: 0
            })
            clearPlpScrollEntry(asPathRef.current)
            setIsRestoring(false)
        }

        // Se aplica en el primer tick en que el listado está listo (sin
        // depender de que `ready` se mantenga estable: el scroll infinito puede
        // volver a ponerlo en "cargando" enseguida) y, como respaldo, al vencer
        // el timeout aunque los productos no hayan terminado de llegar.
        const poll = window.setInterval(() => {
            if (readyRef.current) apply()
        }, PLP_SCROLL_READY_POLL_MS)
        const hardTimeout = window.setTimeout(
            () => apply(true),
            PLP_SCROLL_RESTORE_TIMEOUT_MS
        )

        applyRef.current = apply

        return () => {
            cancelled = true
            applyRef.current = null
            window.clearInterval(poll)
            window.clearTimeout(hardTimeout)
        }
    }, [entry, scrollOffset])

    // Primer render con el listado listo: aplicar de inmediato (el sondeo y el
    // timeout de arriba quedan como respaldo).
    useEffect(() => {
        if (ready && entry && !doneRef.current) applyRef.current?.()
    }, [ready, entry])

    return { isRestoring }
}
